import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Family History v2 API client (api-family-history-v2-service). Mirrors the
 * earlier v2 clients (CC / Dx / Med / Add).
 *
 * Contract notes:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 2. Relationship is an enum ("father" | "mother" | ... | "other"). When
 *    relationship = "other", `customRelation` is required.
 *
 * 3. Duplicate-parent (two Fathers / two Mothers) is intentionally frontend-
 *    only — the backend never rejects. UI shows a soft warning.
 *
 * 4. Delete is soft — the record stays on-doc with deleted=true. There's no
 *    undelete endpoint today; the list response filters out deleted rows.
 */
class FamilyHistoryService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.familyHistoryV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add a relative. Body shape matches beans.AddRequest:
   *   { relationship, customRelation?, conditions: [{name, icdCode?, source}],
   *     description?, expectedVersion }
   */
  add(patientId, body, onResponse, onError) {
    const url = APIConfig.familyHistoryV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Edit an existing relative. Same body shape as add; server performs a full
   * replace of the mutable fields (relationship, customRelation, conditions,
   * description) and preserves AddedBy/AddedAt.
   */
  update(patientId, relativeId, body, onResponse, onError) {
    const url = APIConfig.familyHistoryV2.update.format(patientId, relativeId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Soft-delete a relative. Body carries `expectedVersion` for optimistic
   * locking. APIService has no dedicated DELETE helper, so this goes direct
   * to fetch (same shape as Medicine v2's delete).
   */
  delete(patientId, relativeId, expectedVersion, onResponse, onError) {
    const url = APIConfig.familyHistoryV2.delete.format(patientId, relativeId);
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default FamilyHistoryService;
